import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import InputField from './InputField';
import TextAreaField from './TextAreaField';
import DropdownButton from './DropdownButton';
import Button from './Button';
import ToggleSwitch from './ToggleSwitch';

interface AddNewRoleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    description: string;
    salary: number;
    salaryType: 'point' | 'stamp';
    salaryPeriod: 'daily' | 'weekly' | 'monthly';
    maxCount: number;
    isPointManager: boolean;
    pointAutoPay: boolean;
  }) => void;
}

const AddNewRoleDialog: React.FC<AddNewRoleDialogProps> = ({
  isOpen,
  onClose,
  onSubmit
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [position, setPosition] = useState('부장');
  const [maxCount, setMaxCount] = useState(2);
  const [salary, setSalary] = useState(230);
  const [salaryType, setSalaryType] = useState<'point' | 'stamp'>('point');
  const [salaryPeriod, setSalaryPeriod] = useState<'daily' | 'weekly' | 'monthly'>('monthly');
  const [isPointManager, setIsPointManager] = useState(false);
  const [pointAutoPay, setPointAutoPay] = useState(true);

  // 다이얼로그가 열릴 때 body 스크롤 방지
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!name.trim()) {
      return;
    }

    onSubmit({
      name: name.trim(),
      description: description.trim(),
      salary,
      salaryType,
      salaryPeriod,
      maxCount,
      isPointManager,
      pointAutoPay
    });

    // 폼 초기화
    setName('');
    setDescription('');
    setPosition('부장');
    setMaxCount(2);
    setSalary(230);
    setSalaryType('point');
    setSalaryPeriod('monthly');
    setIsPointManager(false);
    setPointAutoPay(true);
  };

  const handleCancel = () => {
    // 폼 초기화
    setName('');
    setDescription('');
    setPosition('부장');
    setMaxCount(2);
    setSalary(230);
    setSalaryType('point');
    setSalaryPeriod('monthly');
    setIsPointManager(false);
    setPointAutoPay(true);
    onClose();
  };

  const handleDecreaseMaxCount = () => {
    if (maxCount > 1) {
      setMaxCount(maxCount - 1);
    }
  };

  const handleIncreaseMaxCount = () => {
    setMaxCount(maxCount + 1);
  };

  // Portal을 사용하여 body에 직접 렌더링 (레이아웃 transform 영향 회피)
  return createPortal(
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0, 0, 0, 0.5)',
        zIndex: 99999,
        overflow: 'hidden',
        pointerEvents: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
      onClick={(e) => {
        // 배경 클릭 시 닫기
        if (e.target === e.currentTarget) {
          handleCancel();
        }
      }}
    >
      <div 
        style={{
          width: '688px',
          maxWidth: 'calc(100vw - 40px)',
          maxHeight: 'calc(100vh - 40px)',
          background: 'white',
          borderRadius: '16px',
          padding: '24px 28px',
          display: 'flex',
          flexDirection: 'column',
          gap: '141px',
          overflow: 'auto',
          boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.12)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 및 내용 */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '40px'
        }}>
          {/* 헤더 */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '8px'
          }}>
            <div style={{
              flex: '1 1 0',
              display: 'flex',
              flexDirection: 'column',
              gap: '2px'
            }}>
              <div style={{
                color: '#161616',
                fontSize: '20px',
                fontFamily: 'Pretendard',
                fontWeight: '600',
                lineHeight: '30px'
              }}>
                새 역할 생성하기
              </div>
              <div style={{
                color: '#989898',
                fontSize: '16px',
                fontFamily: 'Pretendard',
                fontWeight: '500',
                lineHeight: '24px'
              }}>
                원하시는 역할을 새롭게 생성해 주세요
              </div>
            </div>
            <div
              onClick={handleCancel}
              style={{
                padding: '4px',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <div style={{
                width: '24px',
                height: '24px',
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <img
                  src="/images/icons/closeIcon.svg"
                  alt="닫기"
                  style={{
                    width: '13.20px',
                    height: '13.20px'
                  }}
                />
              </div>
            </div>
          </div>

          {/* 입력 필드 영역 */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '56px'
          }}>
            {/* 역할 이름 및 설명 */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                height: '140px'
              }}>
                <InputField
                  value={name}
                  onChange={setName}
                  placeholder="역할을 입력해 주세요"
                  width="100%"
                  height="48px"
                />
                <TextAreaField
                  value={description}
                  onChange={setDescription}
                  placeholder="역할에 대한 설명을 입력해 주세요"
                  width="100%"
                  height="84px"
                  rows={3}
                />
              </div>

              {/* 직급, 최대 할당 수, 급여, 급여 종류 */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    gap: '16px',
                    flexWrap: 'wrap'
                  }}>
                    {/* 직급 */}
                    <div style={{
                      width: '112px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      <div style={{
                        color: '#161616',
                        fontSize: '16px',
                        fontFamily: 'Pretendard',
                        fontWeight: '600',
                        lineHeight: '24px'
                      }}>
                        직급
                      </div>
                      <DropdownButton
                        options={[
                          { value: '부장', label: '부장' },
                          { value: '조원', label: '조원' }
                        ]}
                        value={position}
                        onChange={setPosition}
                        width="100%"
                        height="48px"
                        size="small"
                      />
                    </div>

                    {/* 최대 할당 가능한 학생 수 */}
                    <div style={{
                      width: '191px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      <div style={{
                        color: '#161616',
                        fontSize: '16px',
                        fontFamily: 'Pretendard',
                        fontWeight: '600',
                        lineHeight: '24px'
                      }}>
                        최대 할당 가능한 학생 수
                      </div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px 16px',
                        background: 'white',
                        borderRadius: '8px',
                        border: '1.2px solid #F0F0F0'
                      }}>
                        <div
                          onClick={handleDecreaseMaxCount}
                          style={{
                            width: '24px',
                            height: '24px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M5.4 11.4H18.6V12.6H5.4V11.4Z" fill="#0D0D0D" />
                          </svg>
                        </div>
                        <div style={{
                          flex: '1 1 0',
                          textAlign: 'center',
                          color: '#161616',
                          fontSize: '13px',
                          fontFamily: 'Pretendard',
                          fontWeight: '500',
                          lineHeight: '19.50px'
                        }}>
                          {maxCount}
                        </div>
                        <div
                          onClick={handleIncreaseMaxCount}
                          style={{
                            width: '24px',
                            height: '24px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M12 5.4V18.6M5.4 12H18.6" stroke="#0D0D0D" strokeWidth="1.2" strokeLinecap="round" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* 급여 */}
                    <div style={{
                      width: '166px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      <div style={{
                        color: '#161616',
                        fontSize: '16px',
                        fontFamily: 'Pretendard',
                        fontWeight: '600',
                        lineHeight: '24px'
                      }}>
                        급여
                      </div>
                      <InputField
                        type="number"
                        value={salary.toString()}
                        onChange={(v) => setSalary(Number(v))}
                        placeholder="급여"
                        width="100%"
                        height="48px"
                      />
                    </div>

                    {/* 급여 종류 */}
                    <div style={{
                      flex: '1 1 0',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      minWidth: '112px'
                    }}>
                      <div style={{
                        color: '#161616',
                        fontSize: '16px',
                        fontFamily: 'Pretendard',
                        fontWeight: '600',
                        lineHeight: '24px'
                      }}>
                        급여 종류
                      </div>
                      <DropdownButton
                        options={[
                          { value: 'point', label: '포인트' },
                          { value: 'stamp', label: '도장' }
                        ]}
                        value={salaryType}
                        onChange={(v) => setSalaryType(v as 'point' | 'stamp')}
                        width="100%"
                        height="48px"
                        size="small"
                      />
                    </div>

                    {/* 급여 지급 주기 */}
                    <div style={{
                      width: '166px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px'
                    }}>
                      <div style={{
                        color: '#161616',
                        fontSize: '16px',
                        fontFamily: 'Pretendard',
                        fontWeight: '600',
                        lineHeight: '24px'
                      }}>
                        급여 지급 주기
                      </div>
                      <DropdownButton
                        options={[
                          { value: 'daily', label: '일급' },
                          { value: 'weekly', label: '주급' },
                          { value: 'monthly', label: '월급' }
                        ]}
                        value={salaryPeriod}
                        onChange={(v) => setSalaryPeriod(v as 'daily' | 'weekly' | 'monthly')}
                        width="100%"
                        height="48px"
                        size="small"
                      />
                    </div>
                  </div>

                  {/* 직급 추가하기 버튼 */}
                  <div style={{
                    height: '48px',
                    padding: '9px 264px',
                    background: '#F8F8F8',
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    cursor: 'pointer'
                  }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <circle cx="10" cy="10" r="8" stroke="#989898" strokeWidth="1.2" />
                        <path d="M10 6V14M6 10H14" stroke="#989898" strokeWidth="1.2" strokeLinecap="round" />
                      </svg>
                    </div>
                    <div style={{
                      color: '#989898',
                      fontSize: '13px',
                      fontFamily: 'Pretendard',
                      fontWeight: '500',
                      lineHeight: '19.50px'
                    }}>
                      직급 추가하기
                    </div>
                  </div>
                </div>

                {/* 토글 스위치 */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                  gap: '40px'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    <div style={{
                      color: '#161616',
                      fontSize: '16px',
                      fontFamily: 'Pretendard',
                      fontWeight: '600',
                      lineHeight: '24px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      포인트 관리자 여부
                    </div>
                    <ToggleSwitch
                      checked={isPointManager}
                      onChange={setIsPointManager}
                      size="medium"
                    />
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    <div style={{
                      color: '#161616',
                      fontSize: '16px',
                      fontFamily: 'Pretendard',
                      fontWeight: '600',
                      lineHeight: '24px',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      포인트 자동 지급 여부
                    </div>
                    <ToggleSwitch
                      checked={pointAutoPay}
                      onChange={setPointAutoPay}
                      size="medium"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 버튼 영역 */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-start',
          alignItems: 'center',
          gap: '8px',
          height: '45px'
        }}>
          <Button
            variant="cancel"
            onClick={handleCancel}
            width="135px"
            height="100%"
          >
            취소
          </Button>
          <Button
            variant="submit"
            onClick={handleSubmit}
            width="489px"
            height="100%"
          >
            생성
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddNewRoleDialog;
